import * as React from 'react'

import {
  DragLayerMonitorProps,
  DropOptions,
  NodeModel,
  PlaceholderRenderParams,
  RenderParams,
  Tree as ReactDndTree,
  TreeMethods,
  useDragOver,
} from '@minoru/react-dnd-treeview'
import { ChevronsDownUp, ChevronsUpDown, PlusIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { IPermitTypeField, IWorkflowStep } from '~/client/graph'
import { getFieldTypeCaption } from '~/client/src/desktop/constants/formFieldsCaptionMap'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import { Button } from '~/client/src/shared/components/Button/Button'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { expandablePermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FormFieldsModal from '../FormFieldsModal'
import FormFieldConfigurator from './components/FormFieldConfigurator'
import FormConfiguratorStore, { TREE_ROOT_ID } from './FormConfigurator.store'

interface IProps {
  workflowStep: IWorkflowStep
  isInitialFormStep: boolean
  workflowType: string
  filterFieldsMap?: Map<string, IPermitTypeField>
  availableFilterFieldsOptions?: IFormFilterFieldOption[]
  allWorkflowSteps?: IWorkflowStep[]

  onWorkflowStepChange(workflowStep: IWorkflowStep): void
  onNotificationFieldStepsChange?(fieldId: string, stepIds: string[]): void
  onRemoveFieldFromNotifications?(fieldId: string): void
}

const DROP_TARGET_OFFSET = 10
const DEPTH_PADDING = 30

@observer
export default class FormConfigurator extends React.Component<IProps> {
  private readonly store: FormConfiguratorStore
  private treeRef: TreeMethods

  public constructor(props: IProps) {
    super(props)

    this.store = new FormConfiguratorStore(
      props.workflowStep,
      props.isInitialFormStep,
      props.workflowType,
      props.availableFilterFieldsOptions,
      props.filterFieldsMap,
      props.onWorkflowStepChange,
      props.onRemoveFieldFromNotifications,
    )
  }

  public componentDidMount() {
    Promise.resolve().then(() => {
      this.treeRef?.open(this.store.openedFieldIds)
    })
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const {
      workflowStep,
      isInitialFormStep,
      workflowType,
      availableFilterFieldsOptions,
      filterFieldsMap,
    } = this.props

    if (workflowStep !== prevProps.workflowStep) {
      this.store.setWorkflowStep(workflowStep)

      Promise.resolve().then(() => {
        this.treeRef?.open(this.store.openedFieldIds)
      })
    }
    if (isInitialFormStep !== prevProps.isInitialFormStep) {
      this.store.setIsInitialFormStep(isInitialFormStep)
    }
    if (workflowType !== prevProps.workflowType) {
      this.store.setWorkflowType(workflowType)
    }
    if (
      availableFilterFieldsOptions !== prevProps.availableFilterFieldsOptions ||
      availableFilterFieldsOptions.length !==
        prevProps.availableFilterFieldsOptions.length
    ) {
      this.store.setAvailableFilterFieldsOptions(availableFilterFieldsOptions)
    }
    if (
      filterFieldsMap !== prevProps.filterFieldsMap ||
      filterFieldsMap.size !== prevProps.filterFieldsMap.size
    ) {
      this.store.setFilterFieldsMap(filterFieldsMap)
    }
  }

  public render() {
    const {
      fields,
      addNewField,
      onChangeFields,
      treeData,
      isRemoveDialogShown,
      hideRemoveDialog,
      removeField,
      restrictedFields,
    } = this.store

    return (
      <>
        <ConfirmDialog
          useRedConfirmationButton
          title={Localization.translator.deleteField}
          doneTitle={Localization.translator.delete}
          onDoneClicked={removeField}
          isOpen={isRemoveDialogShown}
          onCancelClicked={hideRemoveDialog}
        >
          {Localization.translator.deleteFieldQuestion}
        </ConfirmDialog>
        {!fields?.length && (
          <div className="tw:flex tw:justify-center tw:mb-6">
            <FormFieldsModal
              addNewField={addNewField}
              restrictedFields={restrictedFields}
            >
              <Button variant="primary-ghost" size="lg">
                <PlusIcon data-icon="inline-start" />
                {Localization.translator.addField}
              </Button>
            </FormFieldsModal>
          </div>
        )}
        {!!fields?.length && (
          <div className="tw:sticky tw:top-0 tw:z-10 tw:bg-background tw:flex tw:items-center tw:gap-2 tw:px-5 tw:py-2 tw:border-b tw:border-border">
            {fields.some(f => expandablePermitFieldTypes.includes(f.type)) && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={this.store.toggleAllFieldSettings}
                aria-label={
                  this.store.allFieldSettingsCollapsed
                    ? Localization.translator.showAllSettings
                    : Localization.translator.hideAllSettings
                }
              >
                {this.store.allFieldSettingsCollapsed ? (
                  <ChevronsUpDown className="tw:size-4" />
                ) : (
                  <ChevronsDownUp className="tw:size-4" />
                )}
              </Button>
            )}
            <span className="tw:text-[13px] tw:leading-[18px] tw:text-muted-foreground">
              {Localization.translator.xFieldsAdded(fields.length)}
            </span>
          </div>
        )}
        <ReactDndTree
          ref={this.setTreeElement}
          classes={{
            root: 'relative pb40 ma0',
            draggingSource: 'opacity3',
            placeholder: 'relative',
            dropTarget: 'bg-palette-brand-lightest brada4',
          }}
          rootId={TREE_ROOT_ID}
          tree={treeData}
          sort={false}
          insertDroppableFirst={false}
          dropTargetOffset={DROP_TARGET_OFFSET}
          canDrop={this.canDropField}
          render={this.renderNodeRow}
          placeholderRender={this.renderPlaceholder}
          dragPreviewRender={this.renderDragPreview}
          onDrop={onChangeFields}
        />
      </>
    )
  }

  private renderNodeRow = (
    node: NodeModel<IPermitTypeField>,
    { depth, isOpen, onToggle }: RenderParams,
  ): JSX.Element => {
    const onCollapseClick = this.onCollapseClick.bind(
      null,
      onToggle,
      node.id,
      !isOpen,
    )
    const dragOverProps = useDragOver(node.id, isOpen, onCollapseClick)

    return (
      <div
        style={{
          paddingInlineStart: depth * DEPTH_PADDING,
        }}
        {...dragOverProps}
      >
        <FormFieldConfigurator
          depth={depth}
          field={node.data}
          store={this.store}
          isSectionExpanded={isOpen}
          onSectionToggle={onCollapseClick}
          allWorkflowSteps={this.props.allWorkflowSteps}
          onNotificationFieldStepsChange={
            this.props.onNotificationFieldStepsChange
          }
        />
      </div>
    )
  }

  private renderPlaceholder = (
    _node: NodeModel<IPermitTypeField>,
    { depth }: PlaceholderRenderParams,
  ): JSX.Element => {
    return (
      <div
        className="absolute bg-color-blue-brand drag-placeholder brada4"
        style={{ left: depth * DEPTH_PADDING }}
      />
    )
  }

  private renderDragPreview = ({
    item,
  }: DragLayerMonitorProps<IPermitTypeField>): JSX.Element | null => {
    if (!item.data) return null
    const { type, caption } = item.data

    return (
      <div className="row tw:gap-2.5 bg-grey brada10 mw160 h36 px10 text large bold">
        <PermitFieldIcon fieldName={type} />
        <span className="text-ellipsis">
          {caption || getFieldTypeCaption(type)}
        </span>
      </div>
    )
  }

  private canDropField = (
    tree: NodeModel<IPermitTypeField>[],
    options: DropOptions<IPermitTypeField>,
  ): boolean => {
    return this.store.canDropField(
      tree,
      options.dragSource,
      options.dropTargetId?.toString(),
      options.dropTarget,
    )
  }

  private setTreeElement = (ref: TreeMethods) => {
    this.treeRef = ref
  }

  private onCollapseClick = (
    onToggle: () => void,
    fieldId: string,
    isCollapsed: boolean,
  ) => {
    this.store.collapseField(fieldId, isCollapsed)
    onToggle()
  }
}
